import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Circle, Loader2, XCircle, RefreshCw, FileCheck2, FileX2, FileMinus2, ScanSearch } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#C9A84C";
const POLL_MS = 30000;
const fmt = (s) => (s ? String(s).slice(0, 16).replace("T", " ") + " UTC" : "");

function StepIcon({ state }) {
  if (state === "done") return <CheckCircle2 size={22} style={{ color: "#2FB999" }} />;
  if (state === "failed") return <XCircle size={22} style={{ color: "#C0392B" }} />;
  if (state === "current") return <Loader2 size={22} className="animate-spin" style={{ color: GOLD }} />;
  return <Circle size={22} className="text-[#444]" />;
}

const EVIDENCE = {
  on_record: { label: "ON RECORD", color: "#2FB999", Icon: FileCheck2 },
  not_furnished: { label: "NOT FURNISHED", color: "#C0392B", Icon: FileX2 },
  not_applicable: { label: "NOT FURNISHED · OPTIONAL", color: "#666", Icon: FileMinus2 },
};
const SCRUTINY = {
  awaiting: { label: "AWAITING", color: "#666" },
  in_progress: { label: "UNDER SCRUTINY", color: GOLD },
  cleared: { label: "CLEARED", color: "#2FB999" },
  queried: { label: "QUERIED", color: "#E0A030" },
  not_cleared: { label: "NOT CLEARED", color: "#C0392B" },
};

function EvidenceRegister({ evidence, total }) {
  return (
    <div className="border border-[#333] bg-[#0f0f0f] p-5" data-testid="tracker-evidence-register">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>Evidence register · {total} document{total === 1 ? "" : "s"} on record</div>
      <ul className="mt-4 divide-y divide-dotted divide-[#333]">
        {evidence.map((e) => { const m = EVIDENCE[e.state]; return (
          <li key={e.key} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid={`evidence-${e.key}`} data-state={e.state}>
            <span className="inline-flex items-center gap-2 text-[#D4D4D4]"><m.Icon size={15} style={{ color: m.color }} />{e.label}{e.required && <span className="text-[#C0392B]">*</span>}{e.count > 1 && <span className="font-mono text-[11px] text-[#777]">×{e.count}</span>}</span>
            <span className="font-mono text-[10px] tracking-[0.2em]" style={{ color: m.color }}>{m.label}</span>
          </li>); })}
      </ul>
      <p className="text-[11px] text-[#666] mt-3">* Mandatory. Every document furnished is retained under the claim dossier and is subject to authenticity verification.</p>
    </div>
  );
}

function ScrutinyPanel({ scrutiny }) {
  return (
    <div className="border border-[#333] bg-[#0f0f0f] p-5" data-testid="tracker-scrutiny-panel">
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] inline-flex items-center gap-2" style={{ color: GOLD }}><ScanSearch size={14} /> Trade Desk scrutiny checkpoints</div>
      <ol className="mt-4 divide-y divide-dotted divide-[#333]">
        {scrutiny.map((s, i) => { const m = SCRUTINY[s.state]; return (
          <li key={s.key} className="flex items-start justify-between gap-3 py-2.5 text-sm" data-testid={`scrutiny-${s.key}`} data-state={s.state}>
            <span className="text-[#D4D4D4]"><span className="font-mono text-[#777] mr-2">{String(i + 1).padStart(2, "0")}</span>{s.label}
              {s.note && <div className="text-[12px] mt-1 pl-7" style={{ color: s.state === "queried" ? "#E0A030" : "#8A8A82" }} data-testid={`scrutiny-${s.key}-note`}>Trade Desk: {s.note}</div>}
              {s.at && <div className="font-mono text-[10px] text-[#666] mt-0.5 pl-7">{fmt(s.at)}</div>}
            </span>
            <span className="font-mono text-[10px] tracking-[0.2em] shrink-0 mt-0.5" style={{ color: m.color }}>{m.label}</span>
          </li>); })}
      </ol>
      <p className="text-[11px] text-[#666] mt-3">Checkpoints are marked by the Vinci Trade Desk in real time. A QUERIED checkpoint may be followed by an Evidence Call Notice.</p>
    </div>
  );
}

export const ClaimStatusTracker = ({ token }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/claim-form/${token}/status`)
      .then((r) => { if (alive) { setData(r.data); setErr(""); } })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || "Status unavailable"); });
    load();
    const id = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [token, tick]);

  if (err) return <div className="text-sm text-[#C0392B]" data-testid="claim-tracker-error">{String(err)}</div>;
  if (!data) return <Loader2 className="animate-spin mx-auto" style={{ color: GOLD }} data-testid="claim-tracker-loading" />;

  return (
    <section className="mt-10 text-left border border-[#333] bg-[#141414] p-6 md:p-8" data-testid="claim-status-tracker">
      <div className="flex items-start justify-between gap-4 flex-wrap border-b border-[#333] pb-5">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>Case file · live status of record</div>
          <h2 className="font-serif text-xl text-[#F5F5F5] mt-1">Claim {data.claim_ref} · Order {data.order_ref}</h2>
          <div className="font-mono text-[11px] text-[#888] mt-2 space-x-4">
            <span>FORM {data.form_no}</span><span data-testid="tracker-dossier-no">DOSSIER {data.dossier_no}</span>
            {data.payable_commission_inr != null && <span>PAYABLE ₹{Number(data.payable_commission_inr).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>}
          </div>
        </div>
        <button data-testid="claim-tracker-refresh" onClick={() => setTick((t) => t + 1)} className="inline-flex items-center gap-1 text-xs text-[#888] hover:text-[#F5F5F5] transition-colors">
          <RefreshCw size={12} /> Updated {fmt(data.as_of)}
        </button>
      </div>
      <ol className="mt-8 space-y-0">
        {data.steps.map((s, i) => (
          <li key={s.key} className="relative flex gap-4 pb-8 last:pb-0" data-testid={`tracker-step-${s.key}`} data-state={s.state}>
            {i < data.steps.length - 1 && <span className="absolute left-[11px] top-7 bottom-0 w-px" style={{ background: s.state === "done" ? "#2FB999" : "#333" }} />}
            <div className="shrink-0 bg-[#141414]"><StepIcon state={s.state} /></div>
            <div className="flex-1">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className={`text-[15px] ${s.state === "pending" ? "text-[#666]" : "text-[#F5F5F5]"} ${s.state === "current" ? "font-semibold" : ""}`}><span className="font-mono text-[11px] text-[#777] mr-2">STAGE {i + 1}</span>{s.label}</span>
                {s.at && <span className="font-mono text-[11px] text-[#888]" data-testid={`tracker-step-${s.key}-at`}>{fmt(s.at)}</span>}
              </div>
              {s.note && <p className="text-sm mt-1" style={{ color: s.state === "failed" ? "#E07B72" : "#A0A0A0" }} data-testid={`tracker-step-${s.key}-note`}>{s.note}</p>}
            </div>
          </li>
        ))}
      </ol>
      <div className="grid md:grid-cols-2 gap-5 mt-8">
        <EvidenceRegister evidence={data.evidence} total={data.documents_on_record} />
        <ScrutinyPanel scrutiny={data.scrutiny} />
      </div>
      <div className="mt-6 border-l-2 pl-4 text-[12px] text-[#888] leading-relaxed" style={{ borderColor: GOLD }} data-testid="tracker-notice">
        <b className="text-[#D4D4D4]">Notice to claimant.</b> This claim is processed under the Vinci Indirect Commission Protocol and is subject to multi-tier scrutiny. Every statement is verified against the evidence furnished; Vinci reserves the right to call for further documents, seek supplier confirmation and withhold settlement pending verification. Misstatement or unsupported particulars result in forfeiture of the claim. This page refreshes every 30 seconds — quote dossier <span className="font-mono text-[#aaa]">{data.dossier_no}</span> in all correspondence.
      </div>
    </section>
  );
};
